import React, { useMemo } from 'react'
import { Link as RouterLink } from 'react-router-dom'
import {
  Box,
  Paper,
  Stack,
  Typography,
  Button,
  Skeleton,
} from '@mui/material'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import FlagOutlinedIcon from '@mui/icons-material/FlagOutlined'
import {
  ExceptionDomainIcon,
  domainToSlug,
  getDomainMeta,
} from '../../SystemIcons'
import { poolwerxColors } from '../../../theme/colors'
import { generateInspectorUrl } from '../../../utils/urls'
import { useInspector } from '../../../contexts/InspectorContext'
import {
  bandHex,
  BAND_RANK,
  type DomainScore,
  type HealthBand,
} from './healthScore'
import { SALES_COMPARE_BASE_CODE } from '../../../config/exceptions/sales/compareExceptions'

const MAX_VISIBLE = 3

interface AttentionItem {
  key: string
  rank: number
  band: HealthBand
  domain: string
  label: string
  count: number
  ctaTo: string
}

function sumCounts(domainCounts: Record<string, number> | undefined): number {
  if (!domainCounts) return 0
  return Object.values(domainCounts).reduce((a, b) => a + b, 0)
}

// Sales has a dedicated comparison view (base sales-compare code) with
// a mismatch chip strip. For sales the dashboard tile bypasses the cards
// grid and lands the analyst directly on that view; the breadcrumb
// still walks back to the cards grid for the other sales exception
// types (sync errors etc.). Constant lives in compareExceptions.ts.
function buildAttentionItems(
  fpCode: string,
  healthScores: Record<string, DomainScore> | undefined,
  exceptionCounts: Record<string, Record<string, number>> | undefined,
  id?: number,
): AttentionItem[] {
  if (!exceptionCounts) return []

  const items: AttentionItem[] = []

  Object.entries(exceptionCounts).forEach(([domain, counts]) => {
    const total = sumCounts(counts)
    if (total <= 0) return

    const meta = getDomainMeta(domain)
    const slug = domainToSlug(meta.key)

    // Backend stores health scores keyed by the same detailed domain
    // (sale, stock_management, product_setup, product_pricing, system).
    // Look up directly — no rollup remap.
    const band: HealthBand = healthScores?.[domain]?.band ?? 'Watch'
    const bandRank = BAND_RANK[band]

    const ctaTo =
      domain === 'sale'
        ? generateInspectorUrl(fpCode, { id, subpath: `exceptions/${slug}/${SALES_COMPARE_BASE_CODE}` })
        : generateInspectorUrl(fpCode, { id, subpath: `exceptions/${slug}` })

    items.push({
      key: domain,
      rank: bandRank * 10000 + total,
      band,
      domain,
      label: meta.label,
      count: total,
      ctaTo,
    })
  })

  items.sort((a, b) => b.rank - a.rank)
  return items
}

interface BandChipProps {
  band: HealthBand
}

const BandChip: React.FC<BandChipProps> = ({ band }) => {
  const hex = bandHex(band)
  return (
    <Box
      sx={{
        px: 0.875,
        py: 0.125,
        borderRadius: 1,
        bgcolor: `${hex}15`,
        color: hex,
        fontSize: '0.7rem',
        fontWeight: 700,
        letterSpacing: 0.4,
        textTransform: 'uppercase',
        lineHeight: 1.6,
        flexShrink: 0,
      }}
    >
      {band}
    </Box>
  )
}

interface AttentionPanelProps {
  fpCode: string
  healthScores?: Record<string, DomainScore>
  exceptionCounts?: Record<string, Record<string, number>>
  loading?: boolean
}

export const AttentionPanel: React.FC<AttentionPanelProps> = ({
  fpCode,
  healthScores,
  exceptionCounts,
  loading = false,
}) => {
  const { franchiseData } = useInspector()
  const tenureId = franchiseData?.id
  const allItems = useMemo(
    () => buildAttentionItems(fpCode, healthScores, exceptionCounts, tenureId),
    [fpCode, healthScores, exceptionCounts, tenureId],
  )
  const visible = allItems.slice(0, MAX_VISIBLE)
  const totalExceptions = allItems.reduce((sum, i) => sum + i.count, 0)

  return (
    <Paper
      variant="outlined"
      sx={{
        p: { xs: 2, sm: 2.5 },
        height: '100%',
        borderRadius: 2,
        borderColor: 'grey.200',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1}>
        <FlagOutlinedIcon sx={{ fontSize: 18, color: poolwerxColors.teal }} />
        <Typography
          variant="overline"
          color="text.secondary"
          sx={{ letterSpacing: 0.8, fontWeight: 600 }}
        >
          Needs your attention
        </Typography>
      </Stack>

      {loading ? (
        <Stack spacing={1.5} sx={{ mt: 2 }}>
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} variant="rounded" height={44} />
          ))}
        </Stack>
      ) : visible.length === 0 ? (
        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mt: 2, flex: 1 }}>
          <CheckCircleOutlineIcon sx={{ color: 'success.main', fontSize: 32 }} />
          <Box>
            <Typography variant="body1" sx={{ fontWeight: 600 }}>
              All clear
            </Typography>
            <Typography variant="body2" color="text.secondary">
              No open exceptions across your data today.
            </Typography>
          </Box>
        </Stack>
      ) : (
        <>
          <Stack spacing={0.5} sx={{ mt: 1.5, flex: 1 }}>
            {visible.map((item) => (
              <Stack
                key={item.key}
                direction="row"
                alignItems="center"
                spacing={1.5}
                component={RouterLink}
                to={item.ctaTo}
                sx={{
                  px: 1.25,
                  py: 1,
                  borderRadius: 1.5,
                  textDecoration: 'none',
                  color: 'text.primary',
                  transition: 'background-color 0.15s',
                  '&:hover': {
                    bgcolor: 'grey.50',
                    '& .cta-arrow': { transform: 'translateX(4px)' },
                  },
                }}
              >
                <ExceptionDomainIcon
                  domain={item.domain}
                  variant="outlined"
                  size={20}
                  applyColor
                />
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 600, flex: 1, minWidth: 0 }}
                >
                  {item.label}
                </Typography>
                <BandChip band={item.band} />
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 700, minWidth: 48, textAlign: 'right' }}
                >
                  {item.count.toLocaleString()}
                </Typography>
                <ArrowForwardIcon
                  className="cta-arrow"
                  fontSize="small"
                  sx={{ color: 'text.secondary', transition: 'transform 0.15s' }}
                />
              </Stack>
            ))}
          </Stack>

          <Button
            component={RouterLink}
            to={generateInspectorUrl(fpCode, { id: tenureId, subpath: 'exceptions' })}
            endIcon={<ArrowForwardIcon />}
            size="small"
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              px: 0,
              mt: 1,
              alignSelf: 'flex-start',
              '&:hover': { bgcolor: 'transparent' },
            }}
          >
            View all exceptions ({totalExceptions.toLocaleString()})
          </Button>
        </>
      )}
    </Paper>
  )
}

export default AttentionPanel
